import DOMPurify from "dompurify";

/**
 * Composable for sanitizing HTML content to prevent XSS attacks.
 * Uses DOMPurify to clean potentially malicious HTML.
 */
export const useSanitize = () => {
  /**
   * Sanitize HTML content with a strict allowlist of safe tags and attributes.
   * Used for user-generated content like job descriptions, candidate info, etc.
   */
  const sanitizeHtml = (html: string | null | undefined): string => {
    if (!html) return "";

    // Only run DOMPurify on client-side (window required)
    // On server-side, return raw HTML - it will be sanitized on client hydration
    // This prevents SSR/hydration mismatch where escaped HTML shows as text
    if (typeof window === "undefined") {
      return html;
    }

    return DOMPurify.sanitize(html, {
      // Allow common formatting tags
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "s",
        "strike",
        "ul",
        "ol",
        "li",
        "a",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "pre",
        "code",
        "span",
        "div",
        "hr",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
      ],
      // Only allow safe attributes
      ALLOWED_ATTR: ["href", "target", "rel", "class", "id"],
      // Force all links to open in new tab and add security attributes
      ADD_ATTR: ["target", "rel"],
      // Hooks to modify links for security
      FORBID_TAGS: ["script", "style", "iframe", "form", "input", "object", "embed"],
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
    });
  };

  /**
   * Sanitize and decode line breaks (converts \n to <br>).
   * Used for content that may contain newlines that should be rendered as line breaks.
   */
  const sanitizeWithLineBreaks = (text: string | null | undefined): string => {
    if (!text) return "";

    // First sanitize to remove any malicious content
    const sanitized = sanitizeHtml(text);

    // Then convert newlines to <br> tags (these are safe since we just sanitized)
    return sanitized.replace(/\n/g, "<br>");
  };

  return {
    sanitizeHtml,
    sanitizeWithLineBreaks,
  };
};
